import React from 'react';
import './AdditionalInformation.css';

const AdditionalInformation = ({ formData, updateField, canEdit = true }) => {
    const additional = formData.additional || {};

    const updateAdditional = (field, value) => {
        updateField('additional', {
            ...additional,
            [field]: value
        });
    };

    // nearby places + FAQs live at the top of the form data (where the
    // review, validation and the saved record use them)
    const nearbyPlaces = Array.isArray(formData.nearbyPlaces) ? formData.nearbyPlaces : [];

    const faqs = Array.isArray(formData.faqs) ? formData.faqs : [];

    const handleNearbyPlaceChange = (index, field, value) => {
        const next = [...nearbyPlaces];

        next[index] = {
            ...next[index],
            [field]: value
        };

        updateField('nearbyPlaces', next);
    };

    const addNearbyPlace = () => {
        updateField('nearbyPlaces', [
            ...nearbyPlaces,
            {
                name: '',
                distance: '',
                distanceUnit: 'km',
                description: ''
            }
        ]);
    };

    const removeNearbyPlace = (index) => {
        updateField(
            'nearbyPlaces',
            nearbyPlaces.filter((_, i) => i !== index)
        );
    };

    const handleFaqChange = (index, field, value) => {
        const next = [...faqs];

        next[index] = {
            ...next[index],
            [field]: value
        };

        updateField('faqs', next);
    };

    const addFaq = () => {
        updateField('faqs', [
            ...faqs,
            {
                question: '',
                answer: ''
            }
        ]);
    };

    const removeFaq = (index) => {
        updateField(
            'faqs',
            faqs.filter((_, i) => i !== index)
        );
    };

    const toggleAccessibility = (field) => {
        updateAdditional('accessibility', {
            ...(additional.accessibility || {}),
            [field]: !additional.accessibility?.[field]
        });
    };

    return (
        <div className='additional-information'>
            {/* HEADER */}
            <div className='additional-section-header'>
                <div>
                    <span className='additional-eyebrow'>Additional Information</span>

                    <h2>Help guests know more about your property</h2>

                    <p>
                        Add useful information that can help guests understand the property, surroundings and special
                        arrangements.
                    </p>
                </div>
            </div>

            {/* CHECK-IN / CHECK-OUT */}
            <section className='additional-card'>
                <div className='additional-card-header'>
                    <div>
                        <h3>Check-in & Check-out Information</h3>
                        <p>Tell guests when they can arrive and when they need to leave.</p>
                    </div>
                </div>

                <div className='additional-form-grid'>
                    <div className='additional-field'>
                        <label>Check-in starts from</label>

                        <input
                            type='time'
                            value={additional.checkInFrom || ''}
                            onChange={(e) => updateAdditional('checkInFrom', e.target.value)}
                            disabled={!canEdit}
                        />
                    </div>

                    <div className='additional-field'>
                        <label>Check-in ends at</label>

                        <input
                            type='time'
                            value={additional.checkInUntil || ''}
                            onChange={(e) => updateAdditional('checkInUntil', e.target.value)}
                            disabled={!canEdit}
                        />
                    </div>

                    <div className='additional-field'>
                        <label>Check-out from</label>

                        <input
                            type='time'
                            value={additional.checkOutFrom || ''}
                            onChange={(e) => updateAdditional('checkOutFrom', e.target.value)}
                            disabled={!canEdit}
                        />
                    </div>

                    <div className='additional-field'>
                        <label>Check-out until</label>

                        <input
                            type='time'
                            value={additional.checkOutUntil || ''}
                            onChange={(e) => updateAdditional('checkOutUntil', e.target.value)}
                            disabled={!canEdit}
                        />
                    </div>
                </div>

                <div className='additional-checkbox-row'>
                    <label className='additional-checkbox'>
                        <input
                            type='checkbox'
                            checked={additional.selfCheckIn || false}
                            onChange={(e) => updateAdditional('selfCheckIn', e.target.checked)}
                            disabled={!canEdit}
                        />

                        <span>
                            <strong>Self check-in available</strong>
                            <small>Guests can check in without staff assistance.</small>
                        </span>
                    </label>
                </div>
            </section>

            {/* GUEST INSTRUCTIONS */}
            <section className='additional-card'>
                <div className='additional-card-header'>
                    <div>
                        <h3>Guest Instructions</h3>
                        <p>Add important information guests should know before or during their stay.</p>
                    </div>
                </div>

                <div className='additional-field'>
                    <label>Check-in instructions</label>

                    <textarea
                        rows='4'
                        placeholder='Example: Please call the property manager 30 minutes before arrival...'
                        value={additional.checkInInstructions || ''}
                        onChange={(e) => updateAdditional('checkInInstructions', e.target.value)}
                        disabled={!canEdit}
                    />
                </div>

                <div className='additional-field'>
                    <label>Important information for guests</label>

                    <textarea
                        rows='5'
                        placeholder='Add any important information guests should know...'
                        value={additional.guestInformation || ''}
                        onChange={(e) => updateAdditional('guestInformation', e.target.value)}
                        disabled={!canEdit}
                    />
                </div>
            </section>

            {/* NEARBY PLACES */}
            <section className='additional-card'>
                <div className='additional-card-header additional-card-header-row'>
                    <div>
                        <h3>Nearby Places</h3>
                        <p>Show guests useful places and attractions around the property.</p>
                    </div>

                    <button
                        type='button'
                        className='additional-outline-button'
                        onClick={addNearbyPlace}
                        disabled={!canEdit}
                    >
                        + Add Place
                    </button>
                </div>

                {nearbyPlaces.length === 0 ? (
                    <div className='additional-empty-state'>
                        <span>📍</span>

                        <h4>No nearby places added</h4>

                        <p>Add attractions, restaurants, transport points or other useful locations.</p>
                    </div>
                ) : (
                    <div className='additional-repeat-list'>
                        {nearbyPlaces.map((place, index) => (
                            <div className='additional-repeat-card' key={index}>
                                <div className='additional-repeat-header'>
                                    <strong>Nearby Place {index + 1}</strong>

                                    <button
                                        type='button'
                                        className='additional-remove-button'
                                        onClick={() => removeNearbyPlace(index)}
                                        disabled={!canEdit}
                                    >
                                        Remove
                                    </button>
                                </div>

                                <div className='additional-form-grid'>
                                    <div className='additional-field'>
                                        <label>Place name</label>

                                        <input
                                            type='text'
                                            placeholder='Example: Mysore Palace'
                                            value={place.name || ''}
                                            onChange={(e) => handleNearbyPlaceChange(index, 'name', e.target.value)}
                                            disabled={!canEdit}
                                        />
                                    </div>

                                    <div className='additional-field'>
                                        <label>Distance</label>

                                        <div className='additional-input-group'>
                                            <input
                                                type='number'
                                                min='0'
                                                placeholder='5'
                                                value={place.distance || ''}
                                                onChange={(e) =>
                                                    handleNearbyPlaceChange(index, 'distance', e.target.value)
                                                }
                                                disabled={!canEdit}
                                            />

                                            <select
                                                value={place.distanceUnit || 'km'}
                                                onChange={(e) =>
                                                    handleNearbyPlaceChange(index, 'distanceUnit', e.target.value)
                                                }
                                                disabled={!canEdit}
                                            >
                                                <option value='km'>km</option>

                                                <option value='m'>metres</option>

                                                <option value='mi'>miles</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className='additional-field additional-field-full'>
                                        <label>Description</label>

                                        <input
                                            type='text'
                                            placeholder='Example: Popular tourist attraction'
                                            value={place.description || ''}
                                            onChange={(e) =>
                                                handleNearbyPlaceChange(index, 'description', e.target.value)
                                            }
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* ACCESSIBILITY */}
            <section className='additional-card'>
                <div className='additional-card-header'>
                    <div>
                        <h3>Accessibility</h3>
                        <p>Let guests know about accessibility features available at the property.</p>
                    </div>
                </div>

                <div className='additional-option-grid'>
                    <label className='additional-option-card'>
                        <input
                            type='checkbox'
                            checked={additional.accessibility?.wheelchairAccessible || false}
                            onChange={() => toggleAccessibility('wheelchairAccessible')}
                            disabled={!canEdit}
                        />

                        <div>
                            <strong>♿ Wheelchair accessible</strong>
                            <span>Suitable access for wheelchair users.</span>
                        </div>
                    </label>

                    <label className='additional-option-card'>
                        <input
                            type='checkbox'
                            checked={additional.accessibility?.accessibleParking || false}
                            onChange={() => toggleAccessibility('accessibleParking')}
                            disabled={!canEdit}
                        />

                        <div>
                            <strong>🅿️ Accessible parking</strong>
                            <span>Parking suitable for accessibility needs.</span>
                        </div>
                    </label>

                    <label className='additional-option-card'>
                        <input
                            type='checkbox'
                            checked={additional.accessibility?.groundFloorAccess || false}
                            onChange={() => toggleAccessibility('groundFloorAccess')}
                            disabled={!canEdit}
                        />

                        <div>
                            <strong>🏠 Ground floor access</strong>
                            <span>Important areas are accessible from ground level.</span>
                        </div>
                    </label>

                    <label className='additional-option-card'>
                        <input
                            type='checkbox'
                            checked={additional.accessibility?.accessibleBathroom || false}
                            onChange={() => toggleAccessibility('accessibleBathroom')}
                            disabled={!canEdit}
                        />

                        <div>
                            <strong>🚿 Accessible bathroom</strong>
                            <span>Bathroom facilities support accessibility.</span>
                        </div>
                    </label>
                </div>
            </section>

            {/* TRANSPORT / PARKING */}
            <section className='additional-card'>
                <div className='additional-card-header'>
                    <div>
                        <h3>Parking & Transportation</h3>
                        <p>Provide additional information about reaching and parking at the property.</p>
                    </div>
                </div>

                <div className='additional-form-grid'>
                    <div className='additional-field'>
                        <label>Parking type</label>

                        <select
                            value={additional.parkingType || ''}
                            onChange={(e) => updateAdditional('parkingType', e.target.value)}
                            disabled={!canEdit}
                        >
                            <option value=''>Select parking type</option>

                            <option value='free'>Free parking</option>

                            <option value='paid'>Paid parking</option>

                            <option value='street'>Street parking</option>

                            <option value='private'>Private parking</option>

                            <option value='none'>No parking</option>
                        </select>
                    </div>

                    <div className='additional-field'>
                        <label>Parking capacity</label>

                        <input
                            type='number'
                            min='0'
                            placeholder='Example: 10'
                            value={additional.parkingCapacity || ''}
                            onChange={(e) => updateAdditional('parkingCapacity', e.target.value)}
                            disabled={!canEdit}
                        />
                    </div>
                </div>

                <div className='additional-field'>
                    <label>Transportation information</label>

                    <textarea
                        rows='4'
                        placeholder='Example: Airport pickup is available on request...'
                        value={additional.transportationInfo || ''}
                        onChange={(e) => updateAdditional('transportationInfo', e.target.value)}
                        disabled={!canEdit}
                    />
                </div>
            </section>

            {/* FAQs */}
            <section className='additional-card'>
                <div className='additional-card-header additional-card-header-row'>
                    <div>
                        <h3>Frequently Asked Questions</h3>
                        <p>Answer common questions guests may have before booking.</p>
                    </div>

                    <button type='button' className='additional-outline-button' onClick={addFaq} disabled={!canEdit}>
                        + Add FAQ
                    </button>
                </div>

                {faqs.length === 0 ? (
                    <div className='additional-empty-state'>
                        <span>❓</span>

                        <h4>No FAQs added</h4>

                        <p>Add questions and answers that are useful for guests.</p>
                    </div>
                ) : (
                    <div className='additional-repeat-list'>
                        {faqs.map((faq, index) => (
                            <div className='additional-repeat-card' key={index}>
                                <div className='additional-repeat-header'>
                                    <strong>FAQ {index + 1}</strong>

                                    <button
                                        type='button'
                                        className='additional-remove-button'
                                        onClick={() => removeFaq(index)}
                                        disabled={!canEdit}
                                    >
                                        Remove
                                    </button>
                                </div>

                                <div className='additional-field'>
                                    <label>Question</label>

                                    <input
                                        type='text'
                                        placeholder='Example: Is breakfast included?'
                                        value={faq.question || ''}
                                        onChange={(e) => handleFaqChange(index, 'question', e.target.value)}
                                        disabled={!canEdit}
                                    />
                                </div>

                                <div className='additional-field'>
                                    <label>Answer</label>

                                    <textarea
                                        rows='3'
                                        placeholder='Write the answer...'
                                        value={faq.answer || ''}
                                        onChange={(e) => handleFaqChange(index, 'answer', e.target.value)}
                                        disabled={!canEdit}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* EXTRA NOTES */}
            <section className='additional-card'>
                <div className='additional-card-header'>
                    <div>
                        <h3>Additional Notes</h3>
                        <p>Add anything else that does not fit into the sections above.</p>
                    </div>
                </div>

                <div className='additional-field'>
                    <label>Additional notes</label>

                    <textarea
                        rows='5'
                        placeholder='Add any other useful information about this accommodation...'
                        value={additional.notes || ''}
                        onChange={(e) => updateAdditional('notes', e.target.value)}
                        disabled={!canEdit}
                    />
                </div>
            </section>
        </div>
    );
};

export default AdditionalInformation;
